import { ENROLLMENT_STATUS } from '@/constants';
import { startCase } from 'lodash';

// Mirrors backend IsTerminalEnrollmentStatus: once an enrolment reaches one of
// these states, actions like unenrol and send-reminder are no longer allowed.
export const isEnrollmentTerminal = (status?: string | null): boolean =>
  !!status && [ENROLLMENT_STATUS.COMPLETED, ENROLLMENT_STATUS.PASSED].includes(status);

// Mirrors backend GetTrainingManagerStudentFilter.MatchesEnrollmentStatus for the "completed"
// filter, which counts both "completed" and "passed" (LU/OLC exam courses report "passed"). Kept
// distinct from isEnrollmentTerminal: the two sets coincide today but model different backend rules.
// A stricter check silently drops passed enrolments, e.g. zeroing the my-team demo Training counts.
const COMPLETED_STATUSES = new Set<string>([ENROLLMENT_STATUS.COMPLETED, ENROLLMENT_STATUS.PASSED]);
export const isCompletedCourseStatus = (status?: string | null): boolean =>
  COMPLETED_STATUSES.has((status ?? '').toLowerCase());

const STATUS_LABEL_KEYS: Record<string, string> = {
  [ENROLLMENT_STATUS.COMPLETED]: 'manage.completed',
  [ENROLLMENT_STATUS.PASSED]: 'manage.completed',
  [ENROLLMENT_STATUS.IN_PROGRESS]: 'manage.inProgress',
  [ENROLLMENT_STATUS.NOT_STARTED]: 'manage.notStarted',
  [ENROLLMENT_STATUS.FAILED]: 'manage.failed',
  [ENROLLMENT_STATUS.PENDING_REVIEW]: 'manage.pending',
};

/**
 * The single place a Training Manager enrolment's status becomes a label.
 *
 * Overdue is a MODIFIER, not a status: a past-due enrolment still has a real progress state, so the
 * two are returned separately and rendered as two elements. Collapsing them — which the API used to
 * do — showed "Overdue" for a learner who had failed, hiding the failure entirely.
 *
 * `'overdue'` as an incoming status is the LEGACY server value. It carries no progress state, so
 * only the overdue part is shown. Delete that branch once no deployed API emits it.
 */
export const formatEnrolmentStatus = (
  status: string | null | undefined,
  isOverdue: boolean | undefined,
  translate: (key: string) => string
): { label: string; overdueLabel: string | null } => {
  const normalised = (status ?? '').toLowerCase();
  const legacyOverdue = normalised === 'overdue';
  const labelKey = STATUS_LABEL_KEYS[normalised];

  return {
    // An unknown status still gets the Start Case presentation the cells used before this helper
    // existed, rather than being dumped raw as `pending_something`.
    label: labelKey ? translate(labelKey) : legacyOverdue ? '' : startCase(status ?? ''),
    overdueLabel: isOverdue || legacyOverdue ? translate('manage.overdue') : null,
  };
};
